import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragEndEvent,
} from '@dnd-kit/core';
import { supabase } from '../lib/supabase';
import { READONLY } from '../lib/readonly';
import { useToast } from '../lib/toast';
import { proxyPatchDeal, proxyWonDeal, proxyLostDeal, isProxyConfigured } from '../lib/pipedrive';
import type { Pipeline, Stage, Deal } from '../lib/database.types';

// Pipelines view: a kanban of Pipedrive deals grouped by pipeline → stage.
//
// Read path: Supabase anon client pulls + subscribes to realtime changes
// on tb_pipelines / tb_stages / tb_deals. The pipedrive-sync worker owns
// writes to those tables via service_role, so the SPA just observes.
//
// Write path: drag-drop a card across stages → optimistic local state
// update → proxyPatchDeal(id, { stage_id }) through the worker → worker
// PUTs to Pipedrive → Pipedrive webhook fires → worker upserts the
// canonical row via freshness-gated RPC → Supabase realtime echo
// reconciles any drift. The user never waits on the network.
//
// Within-stage reorder is intentionally not draggable — Pipedrive doesn't
// expose stage-internal ordering, so faking it in the UI would be
// overwritten on the next sync.

export function PipelinesView() {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [activePipelineId, setActivePipelineId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const { push } = useToast();

  // Initial load + realtime subscriptions
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const [{ data: p }, { data: s }, { data: d }] = await Promise.all([
        supabase.from('tb_pipelines').select('*').order('order_nr', { ascending: true, nullsFirst: false }),
        supabase.from('tb_stages').select('*').order('order_nr'),
        supabase.from('tb_deals').select('*').order('sort_rank'),
      ]);
      if (!mounted) return;
      const pipelineList = (p as Pipeline[] | null) ?? [];
      setPipelines(pipelineList);
      setStages((s as Stage[] | null) ?? []);
      setDeals((d as Deal[] | null) ?? []);
      if (pipelineList.length > 0 && activePipelineId === null) {
        setActivePipelineId(pipelineList[0].pipedrive_id);
      }
      setLoading(false);
    };
    load();

    const sub = supabase
      .channel('pipedrive-sync')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tb_deals' },
        (payload) => {
          setDeals((prev) => {
            if (payload.eventType === 'DELETE') {
              return prev.filter((x) => x.pipedrive_id !== (payload.old as Deal).pipedrive_id);
            }
            const next = payload.new as Deal;
            const exists = prev.some((x) => x.pipedrive_id === next.pipedrive_id);
            return exists
              ? prev.map((x) => (x.pipedrive_id === next.pipedrive_id ? next : x))
              : [next, ...prev];
          });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tb_stages' },
        (payload) => {
          setStages((prev) => {
            if (payload.eventType === 'DELETE') {
              return prev.filter((x) => x.pipedrive_id !== (payload.old as Stage).pipedrive_id);
            }
            const next = payload.new as Stage;
            const exists = prev.some((x) => x.pipedrive_id === next.pipedrive_id);
            return exists
              ? prev.map((x) => (x.pipedrive_id === next.pipedrive_id ? next : x))
              : [...prev, next];
          });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tb_pipelines' },
        (payload) => {
          setPipelines((prev) => {
            if (payload.eventType === 'DELETE') {
              return prev.filter((x) => x.pipedrive_id !== (payload.old as Pipeline).pipedrive_id);
            }
            const next = payload.new as Pipeline;
            const exists = prev.some((x) => x.pipedrive_id === next.pipedrive_id);
            return exists
              ? prev.map((x) => (x.pipedrive_id === next.pipedrive_id ? next : x))
              : [...prev, next];
          });
        },
      )
      .subscribe();

    return () => {
      mounted = false;
      sub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activePipeline = useMemo(
    () => pipelines.find((p) => p.pipedrive_id === activePipelineId) ?? null,
    [pipelines, activePipelineId],
  );

  const stagesForActive = useMemo(() => {
    if (!activePipeline) return [];
    return stages
      .filter((s) => s.pipeline_id === activePipeline.pipedrive_id && s.active)
      .sort((a, b) => a.order_nr - b.order_nr);
  }, [stages, activePipeline]);

  const dealsByStage = useMemo(() => {
    const map = new Map<number, Deal[]>();
    for (const d of deals) {
      if (d.status === 'deleted') continue;
      if (activePipeline && d.pipeline_id !== activePipeline.pipedrive_id) continue;
      const arr = map.get(d.stage_id) ?? [];
      arr.push(d);
      map.set(d.stage_id, arr);
    }
    // Sort each stage's cards by sort_rank (newer update → smaller rank → top)
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.sort_rank ?? 0) - (b.sort_rank ?? 0));
    }
    return map;
  }, [deals, activePipeline]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    if (READONLY) {
      push({ kind: 'warn', title: 'readonly mode', body: 'drag-drop disabled' });
      return;
    }

    // Active = dealId, over = stageId
    const dealId = Number(String(active.id).replace(/^deal-/, ''));
    const newStageId = Number(String(over.id).replace(/^stage-/, ''));
    const deal = deals.find((d) => d.pipedrive_id === dealId);
    if (!deal || deal.stage_id === newStageId) return;

    const previousStage = deal.stage_id;

    // Optimistic local update
    setDeals((prev) =>
      prev.map((d) => (d.pipedrive_id === dealId ? { ...d, stage_id: newStageId } : d)),
    );

    if (!isProxyConfigured()) {
      push({
        kind: 'warn',
        title: 'VITE_PIPEDRIVE_WORKER_URL not set',
        body: 'Local state updated but Pipedrive not called. Deploy the worker and set the env var.',
        ttlMs: 6000,
      });
      return;
    }

    try {
      const result = await proxyPatchDeal(dealId, { stage_id: newStageId });
      if (!result.ok) {
        // Roll back
        setDeals((prev) =>
          prev.map((d) => (d.pipedrive_id === dealId ? { ...d, stage_id: previousStage } : d)),
        );
        push({
          kind: 'error',
          title: 'Pipedrive write failed',
          body: result.error,
          ttlMs: 6000,
        });
        return;
      }
      if (result.warning === 'pipedrive_ok_mirror_failed') {
        push({
          kind: 'warn',
          title: 'Synced to Pipedrive · mirror out of sync',
          body: 'Pipedrive has the change. Local mirror will heal on next reconcile.',
          ttlMs: 5000,
        });
      }
      // Happy path: webhook echo will reconcile drift
    } catch (e) {
      // Network / auth / exception → roll back
      setDeals((prev) =>
        prev.map((d) => (d.pipedrive_id === dealId ? { ...d, stage_id: previousStage } : d)),
      );
      push({
        kind: 'error',
        title: 'Dispatch failed',
        body: e instanceof Error ? e.message : String(e),
        ttlMs: 6000,
      });
    }
  };

  if (loading) {
    return (
      <div className="flex-1 grid place-items-center text-dim font-mono text-sm">
        loading pipelines…
      </div>
    );
  }

  if (pipelines.length === 0) {
    return (
      <div className="flex-1 overflow-auto">
        <div className="max-w-5xl p-8 space-y-6">
          <header className="space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight">Pipelines</h1>
            <p className="text-sm text-muted font-mono">0 pipelines · no data yet</p>
          </header>
          <div className="panel p-6 space-y-2 border-amber/30">
            <div className="text-sm font-semibold text-amberHi">Pipedrive sync not yet running</div>
            <p className="text-xs text-muted font-mono">
              Deploy the contacts/pipedrive sync worker and set the tracked pipeline IDs. The
              SPA reads from tb_pipelines / tb_stages / tb_deals — those tables are empty
              until the worker bootstraps them.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="p-6 pb-0 space-y-3">
        <header className="space-y-2">
          <h1 className="text-3xl font-semibold tracking-tight">Pipelines</h1>
          <p className="text-sm text-muted font-mono">
            {pipelines.length} tracked · {deals.filter((d) => d.status !== 'deleted').length} active deals
          </p>
        </header>

        {/* Pipeline tabs */}
        <div className="flex items-center gap-1 flex-wrap border-b border-border">
          {pipelines.map((p) => {
            const count = deals.filter(
              (d) => d.pipeline_id === p.pipedrive_id && d.status !== 'deleted',
            ).length;
            const isActive = p.pipedrive_id === activePipelineId;
            return (
              <button
                key={p.pipedrive_id}
                onClick={() => setActivePipelineId(p.pipedrive_id)}
                className={
                  'px-4 py-2 text-sm font-mono border-b-2 -mb-px transition-colors ' +
                  (isActive
                    ? 'border-amber text-ink'
                    : 'border-transparent text-muted hover:text-ink')
                }
              >
                {p.name}
                <span className="ml-2 text-[10px] tabular-nums opacity-60">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <div className="flex-1 overflow-x-auto overflow-y-hidden p-6 pt-4">
          <div className="flex items-start gap-4 min-w-max h-full">
            {stagesForActive.map((stage) => (
              <StageColumn
                key={stage.pipedrive_id}
                stage={stage}
                deals={dealsByStage.get(stage.pipedrive_id) ?? []}
                onWon={async (id) => {
                  setDeals((prev) =>
                    prev.map((d) => (d.pipedrive_id === id ? { ...d, status: 'won' } : d)),
                  );
                  try {
                    const res = await proxyWonDeal(id);
                    if (!res.ok) throw new Error(res.error);
                    push({ kind: 'success', title: 'marked won', body: `deal ${id}` });
                  } catch (e) {
                    push({
                      kind: 'error',
                      title: 'failed to mark won',
                      body: e instanceof Error ? e.message : String(e),
                    });
                  }
                }}
                onLost={async (id) => {
                  setDeals((prev) =>
                    prev.map((d) => (d.pipedrive_id === id ? { ...d, status: 'lost' } : d)),
                  );
                  try {
                    const res = await proxyLostDeal(id);
                    if (!res.ok) throw new Error(res.error);
                    push({ kind: 'success', title: 'marked lost', body: `deal ${id}` });
                  } catch (e) {
                    push({
                      kind: 'error',
                      title: 'failed to mark lost',
                      body: e instanceof Error ? e.message : String(e),
                    });
                  }
                }}
              />
            ))}
          </div>
        </div>
      </DndContext>
    </div>
  );
}

function StageColumn({
  stage,
  deals,
  onWon,
  onLost,
}: {
  stage: Stage;
  deals: Deal[];
  onWon: (id: number) => void;
  onLost: (id: number) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `stage-${stage.pipedrive_id}` });
  const total = deals.reduce((sum, d) => sum + (d.value ?? 0), 0);
  const currency = deals.find((d) => d.currency)?.currency ?? 'USD';
  return (
    <div
      ref={setNodeRef}
      className={
        'panel w-[300px] shrink-0 flex flex-col max-h-full overflow-hidden transition-colors ' +
        (isOver ? 'border-amber/70 bg-amber/5' : '')
      }
    >
      <div className="p-3 border-b border-border space-y-1">
        <div className="flex items-center justify-between">
          <div className="text-xs font-mono text-amberHi uppercase tracking-widest truncate">
            {stage.name}
          </div>
          <div className="text-[10px] font-mono text-dim tabular-nums">{deals.length}</div>
        </div>
        {total > 0 && (
          <div className="text-[10px] font-mono text-dim tabular-nums">
            {currency} {total.toLocaleString()}
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {deals.map((d) => (
          <DealCard key={d.pipedrive_id} deal={d} onWon={onWon} onLost={onLost} />
        ))}
        {deals.length === 0 && (
          <div className="text-[11px] text-dim italic font-mono text-center py-6">empty</div>
        )}
      </div>
    </div>
  );
}

function DealCard({
  deal,
  onWon,
  onLost,
}: {
  deal: Deal;
  onWon: (id: number) => void;
  onLost: (id: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `deal-${deal.pipedrive_id}`,
  });
  const style: React.CSSProperties = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 50,
      }
    : {};
  const isWon = deal.status === 'won';
  const isLost = deal.status === 'lost';
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={
        'panel-hi p-3 rounded cursor-grab active:cursor-grabbing space-y-1.5 border transition-all ' +
        (isDragging ? 'opacity-60 ring-2 ring-amber/60' : '') +
        (isWon ? ' border-green/50' : isLost ? ' border-red/40 opacity-70' : ' border-border')
      }
    >
      <div className="text-sm font-medium line-clamp-2">{deal.title}</div>
      <div className="flex items-center gap-2 text-[10px] font-mono text-muted">
        {deal.value != null && (
          <span className="text-amberHi tabular-nums">
            {deal.currency ?? ''} {deal.value.toLocaleString()}
          </span>
        )}
        {deal.org_name && <span className="truncate">· {deal.org_name}</span>}
      </div>
      {deal.person_name && (
        <div className="text-[10px] font-mono text-dim truncate">{deal.person_name}</div>
      )}
      {!isWon && !isLost && (
        <div className="flex items-center gap-1 pt-1 border-t border-border/50">
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onWon(deal.pipedrive_id);
            }}
            className="text-[10px] font-mono text-green hover:text-ink px-2 py-1 rounded border border-border hover:border-green/60 transition-colors"
          >
            won
          </button>
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onLost(deal.pipedrive_id);
            }}
            className="text-[10px] font-mono text-red hover:text-ink px-2 py-1 rounded border border-border hover:border-red/60 transition-colors"
          >
            lost
          </button>
        </div>
      )}
    </div>
  );
}
